// ═══════════════════════════════════════════
// MonArgent Pro — NLP Engine
// Parses informal French / African French messages
// Handles mixed languages, typos, abbreviations
// ═══════════════════════════════════════════

// ─── Category dictionaries with emoji mapping ───
export const CATEGORIES = {
  // Food
  riz: { emoji: "🍚", group: "alimentation" },
  poulet: { emoji: "🍗", group: "alimentation" },
  poisson: { emoji: "🐟", group: "alimentation" },
  tomate: { emoji: "🍅", group: "alimentation" },
  oignon: { emoji: "🧅", group: "alimentation" },
  huile: { emoji: "🫒", group: "alimentation" },
  pain: { emoji: "🍞", group: "alimentation" },
  legumes: { emoji: "🥬", group: "alimentation" },
  fruits: { emoji: "🍌", group: "alimentation" },
  viande: { emoji: "🥩", group: "alimentation" },
  lait: { emoji: "🥛", group: "alimentation" },
  sucre: { emoji: "🍬", group: "alimentation" },
  sel: { emoji: "🧂", group: "alimentation" },
  farine: { emoji: "🌾", group: "alimentation" },
  haricot: { emoji: "🫘", group: "alimentation" },
  mais: { emoji: "🌽", group: "alimentation" },
  manioc: { emoji: "🥔", group: "alimentation" },
  plantain: { emoji: "🍌", group: "alimentation" },
  igname: { emoji: "🥔", group: "alimentation" },
  banane: { emoji: "🍌", group: "alimentation" },
  attieke: { emoji: "🍚", group: "alimentation" },
  garba: { emoji: "🍚", group: "alimentation" },
  alloco: { emoji: "🍌", group: "alimentation" },
  piment: { emoji: "🌶️", group: "alimentation" },

  // Drinks
  boisson: { emoji: "🥤", group: "boisson" },
  jus: { emoji: "🧃", group: "boisson" },
  biere: { emoji: "🍺", group: "boisson" },
  soda: { emoji: "🥤", group: "boisson" },
  cafe: { emoji: "☕", group: "boisson" },
  the: { emoji: "🍵", group: "boisson" },
  eau: { emoji: "💧", group: "boisson" },
  vin: { emoji: "🍷", group: "boisson" },
  bissap: { emoji: "🧃", group: "boisson" },
  gingembre: { emoji: "🧃", group: "boisson" },

  // Transport
  carburant: { emoji: "⛽", group: "transport" },
  essence: { emoji: "⛽", group: "transport" },
  gasoil: { emoji: "⛽", group: "transport" },
  transport: { emoji: "🚗", group: "transport" },
  course: { emoji: "🚕", group: "transport" },
  peage: { emoji: "🛣️", group: "transport" },
  parking: { emoji: "🅿️", group: "transport" },
  vidange: { emoji: "🔧", group: "transport" },
  pneu: { emoji: "🛞", group: "transport" },
  reparation: { emoji: "🔧", group: "transport" },
  lavage: { emoji: "🚿", group: "transport" },
  taxi: { emoji: "🚕", group: "transport" },

  // Business
  loyer: { emoji: "🏠", group: "charges" },
  electricite: { emoji: "💡", group: "charges" },
  telephone: { emoji: "📱", group: "charges" },
  internet: { emoji: "📶", group: "charges" },
  stock: { emoji: "📦", group: "commerce" },
  marchandise: { emoji: "📦", group: "commerce" },
  fourniture: { emoji: "🧾", group: "commerce" },
  materiel: { emoji: "🛠️", group: "commerce" },
  credit: { emoji: "💳", group: "finance" },
  emballage: { emoji: "📦", group: "commerce" },
  livraison: { emoji: "🚚", group: "commerce" },
  commande: { emoji: "📋", group: "commerce" },

  // Restaurant
  table: { emoji: "🍽️", group: "restaurant" },
  menu: { emoji: "📋", group: "restaurant" },
  plat: { emoji: "🍛", group: "restaurant" },
  gaz: { emoji: "🔥", group: "restaurant" },
  charbon: { emoji: "🔥", group: "restaurant" },

  // Salon
  coiffure: { emoji: "💇", group: "beaute" },
  tresse: { emoji: "💇‍♀️", group: "beaute" },
  meche: { emoji: "💇‍♀️", group: "beaute" },
  creme: { emoji: "🧴", group: "beaute" },
  ongle: { emoji: "💅", group: "beaute" },
  maquillage: { emoji: "💄", group: "beaute" },
  defrisage: { emoji: "💇", group: "beaute" },

  // Health
  medicament: { emoji: "💊", group: "sante" },
  consultation: { emoji: "🩺", group: "sante" },
  ordonnance: { emoji: "📋", group: "sante" },

  // Fashion
  vetement: { emoji: "👗", group: "mode" },
  tissu: { emoji: "🧵", group: "mode" },
  chaussure: { emoji: "👟", group: "mode" },
  pagne: { emoji: "🧵", group: "mode" },
  bazin: { emoji: "🧵", group: "mode" },
  wax: { emoji: "🧵", group: "mode" },

  // Agriculture
  engrais: { emoji: "🌱", group: "agriculture" },
  semence: { emoji: "🌱", group: "agriculture" },
  recolte: { emoji: "🌾", group: "agriculture" },
  betail: { emoji: "🐄", group: "agriculture" },
  mouton: { emoji: "🐑", group: "agriculture" },
  chevre: { emoji: "🐐", group: "agriculture" },
  porc: { emoji: "🐷", group: "agriculture" },
  boeuf: { emoji: "🐂", group: "agriculture" },

  // Misc
  savon: { emoji: "🧴", group: "divers" },
  client: { emoji: "👤", group: "commerce" },
  ticket: { emoji: "🎫", group: "commerce" },
  divers: { emoji: "📦", group: "divers" },
};

// ─── Keyword patterns for sale detection ───
const SALE_PATTERNS = [
  /vente/i, /vendu/i, /vend[us]?/i, /j.ai vendu/i,
  /sold/i, /sell/i,
  /re[çc]u/i, /gagn[ée]/i, /encaiss[ée]/i,
  /entr[ée]e/i, /recette/i,
  /client.*pay/i, /pay[ée].*client/i,
];

const EXPENSE_PATTERNS = [
  /achat/i, /achet[ée]/i,
  /pay[ée]/i, /d[ée]pens[ée]/i,
  /bought/i, /spent/i,
  /donn[ée]/i, /sortie/i, /sorti/i,
  /vers[ée]/i,
];

// ─── Main parser ───
export function parseMessage(text) {
  // Normalize: remove accents for matching, keep original for display
  const raw = text.trim();
  const t = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['']/g, "'");

  // ─ Command detection ─
  if (/^(resume|bilan|total|combien|rapport|comment.*march|recap)/.test(t)) {
    return { type: "resume" };
  }
  if (/^(conseil|aide|astuce|tip|comment.*amelior)/.test(t)) {
    return { type: "conseil" };
  }
  if (/^(dette|doit|credit|pret|avance|ardoise)/.test(t)) {
    return { type: "dette", raw };
  }
  if (/^(historique|hier|semaine|mois)/.test(t)) {
    return { type: "historique" };
  }
  if (/^(bonjour|salut|hello|bonsoir|hey|yo|wesh|salam)/.test(t)) {
    return { type: "salut" };
  }
  if (/^(merci|ok|super|genial|bien|nickel|top|parfait|cool)/.test(t)) {
    return { type: "merci" };
  }
  if (/^(aide|help|comment|quoi|mode.*emploi)/.test(t)) {
    return { type: "aide" };
  }

  // Also check non-anchored patterns for resume
  if (/resume|bilan|total|combien|rapport/.test(t)) {
    return { type: "resume" };
  }
  if (/conseil|astuce/.test(t)) {
    return { type: "conseil" };
  }

  // ─ Amount extraction ─
  // Handles: "5000", "5 000", "5.000", "5,000", "5k", "50k"
  let amount = null;
  const amountPatterns = [
    /(\d+)\s*k\b/i,          // "5k" = 5000
    /(\d[\d\s.,]*\d)/,       // "5 000" or "5.000" or "5,000"
    /(\d+)/,                  // simple number
  ];

  for (const pattern of amountPatterns) {
    const match = t.match(pattern);
    if (match) {
      let numStr = match[1].replace(/[\s.,]/g, "");
      amount = parseInt(numStr, 10);
      if (pattern === amountPatterns[0]) amount *= 1000; // "k" multiplier
      if (amount > 0) break;
    }
  }

  // ─ Category extraction ─
  const catKeys = Object.keys(CATEGORIES);
  const foundCat = catKeys.find((key) => t.includes(key));
  const category = foundCat || "divers";

  // ─ Transaction type detection ─
  const isSale = SALE_PATTERNS.some((p) => p.test(t));
  const isExpense = EXPENSE_PATTERNS.some((p) => p.test(t));

  if (isSale && !isExpense) {
    return amount
      ? { type: "vente", amount, category, emoji: getCatEmoji(category) }
      : { type: "unclear", hint: "vente" };
  }

  if (isExpense && !isSale) {
    return amount
      ? { type: "depense", amount, category, emoji: getCatEmoji(category) }
      : { type: "unclear", hint: "depense" };
  }

  // If both matched, use order: first keyword wins
  if (isSale && isExpense) {
    const saleIdx = SALE_PATTERNS.reduce((min, p) => {
      const m = t.match(p);
      return m ? Math.min(min, m.index) : min;
    }, Infinity);
    const expIdx = EXPENSE_PATTERNS.reduce((min, p) => {
      const m = t.match(p);
      return m ? Math.min(min, m.index) : min;
    }, Infinity);

    const type = saleIdx < expIdx ? "vente" : "depense";
    return amount
      ? { type, amount, category, emoji: getCatEmoji(category) }
      : { type: "unclear", hint: type };
  }

  // Amount without clear type
  if (amount) {
    return { type: "unclear", amount, category };
  }

  return { type: "unknown" };
}

export function getCatEmoji(category) {
  return CATEGORIES[category]?.emoji || "📦";
}

export function getCatGroup(category) {
  return CATEGORIES[category]?.group || "divers";
}

// ─── Tips / Conseils ───
export const TIPS = [
  "Notez chaque vente, même 100 FCFA. En 1 mois ça fait la différence !",
  "Séparez TOUJOURS l'argent du business et vos dépenses personnelles.",
  "Achetez en gros quand vous pouvez — jusqu'à 30% d'économie.",
  "Les 3 produits qui se vendent le plus méritent toujours du stock.",
  "Comparez vos ventes chaque semaine. La tendance est votre boussole.",
  "Un client satisfait en amène 3. Un client mécontent en fait fuir 10.",
  "Fixez-vous un objectif de vente CHAQUE matin avant d'ouvrir.",
  "10% du bénéfice dans une caisse d'épargne = votre assurance.",
  "Regardez les prix chez le voisin chaque lundi. Restez compétitif.",
  "Les petites dépenses qu'on oublie de noter sont les pires ennemies du bénéfice.",
  "Proposez un petit cadeau aux 5 meilleurs clients du mois.",
  "Tenez un cahier de dettes — qui doit combien et depuis quand.",
  "Changez votre vitrine ou disposition chaque semaine pour attirer l'œil.",
  "Le meilleur moment pour commander du stock c'est AVANT la rupture.",
  "Évitez les crédits aux gens qui ne remboursent jamais. Votre business d'abord.",
  "Gardez toujours de la monnaie en caisse dès le matin.",
  "Calculez votre bénéfice CHAQUE soir. Pas à la fin du mois.",
  "Un bon emplacement vaut mieux qu'une grande boutique mal placée.",
  "Investissez dans la qualité : les clients reviennent pour la qualité.",
  "Ne mélangez jamais l'argent de 2 activités différentes.",
];

export function getRandomTip() {
  return TIPS[Math.floor(Math.random() * TIPS.length)];
}

export function getDailyTip() {
  const day = new Date().getDate();
  return TIPS[day % TIPS.length];
}
